
import { Options, prop, Vue } from "vue-class-component";
import moment from "moment";
import { AuthController } from "@/app/ui/controllers/AuthController";
import { OtpRequest } from "@/data/payload/api/AuthRequest";
import { recaptchaV3 } from "@/app/infrastructures/misc/common-library/RecaptchaV3";
import { MainAppController } from "../../controllers/MainAppController";
import { TokenVerificationAccountEntities } from "@/domain/entities/Auth";
import { AccountController } from "../../controllers/AccountController";

declare const grecaptcha: {
  render: (
    el: string,
    opt: { sitekey: string; callback: (token: string) => void }
  ) => number;
  reset: (id?: number) => void;
};

class Props {
  modelValue = prop<boolean>({
    default: false,
  });
  password = prop<string>({
    default: "",
  });
  action = prop<string>({
    default: "",
  });
  email = prop<string>({
    default: "",
  });
  newEmail = prop<string>({
    default: ""
  });
  phone = prop<string>({
    default: "",
  });
  errorMessage = prop<string>({
    default: "",
  });
  isWithdraw = prop<boolean>({
    default: false,
    type: Boolean
  });
  isWithContacts = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  emits: ["update:modelValue", "verify"],
  watch: {
    modelValue(newVal) {
      if (newVal) this.onOpen();
    },
  },
})
export default class ModalOtp extends Vue.with(Props) {
  beforeDestroy() {
    this.cleanup();
  }

  onOpen() {
    this.cleanup(); // stop any running intervals, remove scripts
    AccountController.setChangeEmailError("");
    this.otp = [];
    this.otpData = new TokenVerificationAccountEntities();
    this.getErrorMessage = this.errorMessage;
    this.startCountdown();

    this.$nextTick(() => {
      const firstInput = this.otpInputRefs[0];
      firstInput?.focus();
      firstInput?.select();
    });
  }

  onClose() {
    this.$emit("update:modelValue", false);
  }

  // --- 🔐 OTP State ---
  otpInputRefs: any[] = [];
  otp: string[] = [];
  errorOTP = false;
  getErrorMessage = "";

  // --- 📦 UI State ---
  loadingVerifyOtp = false;
  countDown = "";
  canRequestOtp = true;

  // --- 🔁 Countdown & reCAPTCHA ---
  scriptEl: HTMLScriptElement | null = null;
  countDownInterval: NodeJS.Timeout | null = null;
  widgetId: number | null = null;

  // --- 🔧 Getters ---
  get maskedEmail() {
    const { emailUser, emailPartnerClient } = this.otpData;
    return this.isWithdraw
      ? emailPartnerClient || this.email
      : emailUser || this.email;
  }
  get maskedPhone() {
    return this.phone;
  }
  get recaptchaSiteKey() {
    return process.env.VUE_APP_RECAPTCHA_SITEKEY;
  }
  get isOTPValid() {
    return this.otp.filter(Boolean).length === 6;
  }

  get titleEmail() {
    return this.isWithdraw ? "Email partner/klien" : "Email akun";
  }

  get titlePhoneNumber() {
    return this.isWithdraw ? "Nomor telpon partner/klien" : "Nomor telpon akun";
  }

  get changeEmailError() {
    return AccountController.changeEmailError
  }

  unmounted() {
    this.cleanup();
  }

  // --- 🔁 Countdown---
  startCountdown() {
    this.canRequestOtp = false;

    const existing = localStorage.getItem("otp-countdown");
    const endTime = existing ? +existing : Date.now() + 2 * 60 * 1000;

    localStorage.setItem("otp-countdown", String(endTime));

    // Set countdown immediately
    this.updateCountdownDisplay(endTime);

    if (this.countDownInterval) {
      clearInterval(this.countDownInterval);
    }

    this.countDownInterval = setInterval(() => {
      this.updateCountdownDisplay(endTime);
    }, 1000);
  }

  updateCountdownDisplay(endTime: number) {
    const now = Date.now();
    const remaining = Math.max(0, endTime - now);

    if (remaining <= 0) {
      this.countDown = "00:00.";
      this.canRequestOtp = true;
      clearInterval(this.countDownInterval!);
      return;
    }

    // Round to nearest second
    const seconds = Math.round(remaining / 1000) * 1000;
    this.countDown = moment.utc(seconds).format("mm:ss.");
  }

  otpData = new TokenVerificationAccountEntities();
  async requestOtp() {
    this.getErrorMessage = "";
    try {
      const captchaToken = await recaptchaV3.executeRecaptcha("resend_otp");

      MainAppController.showLoading();
      this.otpData = await AuthController.requestOtp(
        new OtpRequest({
          password: this.password,
          captchaToken,
          action: this.action,
          newEmail: this.newEmail
        })
      );

      localStorage.removeItem("otp-countdown");
      this.otp = [];
      this.startCountdown();
    } catch (error) {
      this.getErrorMessage = (error as any)?.response?.data?.message?.id;
    } finally {
      MainAppController.closeLoading();
    }
  }

  cleanup() {
    clearInterval(this.countDownInterval!);
    localStorage.removeItem("otp-countdown");
    this.scriptEl?.remove();
    delete (window as any).__recaptchaLoaded;
  }

  // --- ✅ OTP Verification ---
  async verifyOtp() {
    if (!this.isOTPValid || this.loadingVerifyOtp) return;

    this.$emit("verify", this.otp.join(""), this.otpData.token);
  }

  // --- 🔢 OTP Input Handling ---
  focusNext(index: number) {
    const next = this.otpInputRefs[index + 1];
    next?.focus();
    next?.select();
  }

  focusPrev(index: number) {
    const prev = this.otpInputRefs[index - 1];
    prev?.focus();
    prev?.select();
  }

  onInput(event: any, index: number) {
    const input = event.target as HTMLInputElement;
    let val = input.value;

    // Only keep the first digit if multiple typed/pasted
    if (val.length > 1) {
      val = val.charAt(0);
    }

    // Validate it is a digit, else clear
    if (!/^\d$/.test(val)) {
      this.otp[index] = "";
      input.value = ""; // clear input element value as well
      return;
    }

    this.otp[index] = val;
    input.value = val; // make sure input element value synced (important for paste)

    this.$nextTick(() => {
      this.focusNext(index);
    });
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (this.isModifierKey(event)) return;
    if (this.handleArrowKeys(event, index)) return;
    if (this.handleBackspace(event, index)) return;
    if (this.isAllowedNavigationKey(event)) return;
    if (!this.isValidDigitInput(event)) {
      event.preventDefault();
    }
  }

  isModifierKey(event: KeyboardEvent): boolean {
    return event.ctrlKey || event.metaKey;
  }

  handleArrowKeys(event: KeyboardEvent, index: number): boolean {
    if (event.key === "ArrowLeft") {
      this.focusPrev(index);
      event.preventDefault();
      return true;
    }
    if (event.key === "ArrowRight") {
      this.focusNext(index);
      event.preventDefault();
      return true;
    }
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      return true;
    }
    return false;
  }

  handleBackspace(event: KeyboardEvent, index: number): boolean {
    if (event.key === "Backspace") {
      if (!(event.target as HTMLInputElement).value) {
        this.focusPrev(index);
        event.preventDefault();
      }
      return true;
    }
    return false;
  }

  isAllowedNavigationKey(event: KeyboardEvent): boolean {
    return ["Enter", "NumpadEnter", "Tab"].includes(event.key);
  }

  isValidDigitInput(event: KeyboardEvent): boolean {
    const key = event.key;
    const maxLengthReached = this.otp.join("").length >= 6;
    const isDigit =
      event.code !== "Space" && Number(key) >= 0 && Number(key) <= 9;

    if (!isDigit) return false;
    if (maxLengthReached && event.code !== "Delete") return false;
    return true;
  }

  onPaste(event: ClipboardEvent) {
    const pasted = event.clipboardData?.getData("text") ?? "";
    event.preventDefault();

    if (/^\d{6}$/.test(pasted)) {
      this.otp = pasted.slice(0, 6).split("");

      this.$nextTick(() => {
        const lastInput = this.otpInputRefs[5];
        lastInput?.focus();
        lastInput?.select();
      });
    }
  }
}
